import { Link } from "wouter";
import ArabicToggle from "@/components/ArabicToggle";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SEOHead from "@/components/SEOHead";
import { LOCAL_FINAL_SPECS } from "@/data/localFinalSpec";
import { LOCAL_AEO_STRATEGY_SPECS } from "@/data/localAeoStrategySpec";
import { LocalAeoPlanFactsBlock } from "@/components/LocalAeoStrategyBlocks";
import RelatedToolsAndGuides from "@/components/RelatedToolsAndGuides";
import LocalIndustryGuideLinks from "@/components/LocalIndustryGuideLinks";
import { trackSectionCtaClick } from "@/lib/analytics";
import { ArrowRight, CheckCircle, MapPin, Zap, TrendingUp, Globe, BarChart2, Layout, Search } from "lucide-react";

import SchemaScript from "@/components/SchemaScript";
const LEGACY_FAQS = [
  {
    q: "What does the AI Visibility Plan include for a local business in SW London or Surrey?",
    a: "The AI Visibility Plan (£599/mo) includes everything in the Local Presence plan plus a 5-page AI website optimised for local and AI search, Google Ads management with local campaign targeting, GA4 and attribution setup, weekly content recommendations, and a monthly strategy call. It is designed for businesses in Richmond, Putney, Wimbledon, Kingston, Guildford, and across SW London and Surrey that are ready to move beyond Google Business Profile optimisation and invest in a complete digital presence.",
  },
  {
    q: "How does an AI website differ from a standard website for a local business?",
    a: "An AI website is built with Answer Engine Optimisation (AEO) and GEO (Generative Engine Optimisation) from the ground up. Every page includes structured data schema, speakable markup, FAQ schema, and content structured to answer the specific questions your local customers ask in ChatGPT, Perplexity, Google AI Overviews, and voice search. A standard website is built for human readers and Google's traditional ranking algorithm. An AI website is built to be cited by AI systems as the authoritative answer for queries like 'best gym in Wimbledon' or 'pilates classes near Kingston'.",
  },
  {
    q: "Can Google Ads be targeted specifically to Richmond, Putney, Wimbledon, and Kingston?",
    a: "Yes. We set up hyper-local Google Ads campaigns with radius targeting centred on your business location, targeting customers within specific postcodes in SW London and Surrey. For a cafe in Richmond, we target TW9, TW10, and adjacent postcodes. For a gym in Wimbledon, we target SW19, SW20, and nearby areas. We also use location bid adjustments to increase bids during peak local search times and for mobile users who are physically close to your location.",
  },
  {
    q: "What is included in the weekly content recommendations?",
    a: "Each week you receive three content recommendations based on trending local search queries, seasonal opportunities, and competitor gaps in your area. Recommendations include the specific keyword, the recommended content format (Google Post, blog article, FAQ update, or social post), and a brief content brief. For businesses in Surrey and SW London, we track local event calendars, seasonal patterns, and area-specific search trends to ensure recommendations are always locally relevant.",
  },
  {
    q: "How does the AI Visibility Plan help my business appear in Google AI Overviews?",
    a: "Google AI Overviews draw from pages with strong structured data, clear entity relationships, and content that directly answers specific questions. The AI website included in this plan is built with these signals from day one: Service schema with areaServed pointing to your specific SW London or Surrey locations, FAQPage schema with locally-relevant questions, and Speakable markup on key answer passages. Combined with an optimised Google Business Profile and consistent local citations, this gives your business the best possible chance of appearing in AI Overviews for local queries.",
  },
  {
    q: "Is there a setup fee for the AI Visibility Plan?",
    a: "Yes, there is a one-time setup fee of £149 covering the website build (5 pages), Google Ads account setup, GA4 configuration, schema implementation, and onboarding call. The monthly subscription of £599 then covers ongoing management, weekly recommendations, monthly strategy call, and all updates to your website and campaigns.",
  },
  {
    q: "Can I upgrade from the Local Presence plan to the AI Visibility Plan?",
    a: "Yes. If you are already on the Local Presence plan, your existing schema work, GBP optimisation, and audit history carry forward. The upgrade fee is £99 (reduced from £149) to cover the website build and Google Ads setup. Your monthly subscription changes from £299 to £599.",
  },
  {
    q: "Do you serve businesses in Guildford and other Surrey towns on this plan?",
    a: "Yes. The AI Visibility Plan is available for businesses across the entire SW London and Surrey corridor including Richmond, Putney, Wimbledon, Kingston, Twickenham, Guildford, Epsom, Esher, Cobham, and Woking. For Surrey towns, we implement county-level and district-level schema markup and run Google Ads campaigns with Surrey-specific geographic targeting.",
  },
];

const INCLUDED = [
  { icon: <Layout className="w-5 h-5" />, title: "5-Page AI Website", desc: "AEO and GEO-optimised site with LocalBusiness schema, FAQ schema, and Speakable markup" },
  { icon: <Search className="w-5 h-5" />, title: "Google Ads Management (ad spend not included)", desc: "Hyper-local campaigns with radius targeting for your SW London or Surrey postcode" },
  { icon: <BarChart2 className="w-5 h-5" />, title: "GA4 and Attribution Setup", desc: "Full analytics with conversion tracking, local call tracking, and monthly performance reports" },
  { icon: <TrendingUp className="w-5 h-5" />, title: "Weekly Content Recommendations", desc: "3 locally-relevant content ideas per week based on trending search queries in your area" },
  { icon: <Globe className="w-5 h-5" />, title: "All Local Presence Features", desc: "Weekly report, GBP optimisation, schema markup, review management, and LinkedIn post" },
  { icon: <Zap className="w-5 h-5" />, title: "Monthly Strategy Call", desc: "30-minute call to review performance, adjust targeting, and plan the next month" },
];

export default function ServiceAIVisibilityPlan() {
  const planSpec = LOCAL_FINAL_SPECS["/services/ai-visibility-plan/"];
  const strategySpec = LOCAL_AEO_STRATEGY_SPECS["/services/ai-visibility-plan/"];
  const FAQS = strategySpec?.faqs ?? planSpec?.faqs ?? LEGACY_FAQS;

  const legacySchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/ai-visibility-plan/",
        "url": "https://integrated.social/services/ai-visibility-plan/",
        "name": "AI Visibility Plan: AI Website and Google Ads for SW London and Surrey SMEs | £599/mo",
        "description": "5-page AI website, Google Ads with hyper-local targeting, GA4 attribution, and weekly content recommendations for businesses in Richmond, Putney, Wimbledon, Kingston, Guildford, and across SW London and Surrey. From £599/month.",
        "inLanguage": "en-GB",
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
            { "@type": "ListItem", "position": 3, "name": "AI Visibility Plan", "item": "https://integrated.social/services/ai-visibility-plan/" },
          ],
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": ["h1", "h2", ".speakable-summary"],
        },
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/ai-visibility-plan/#service",
        "name": "AI Visibility Plan",
        "alternateName": ["AI Website for Local Businesses SW London", "Google Ads Local Business Surrey", "AI Search Optimisation Richmond Wimbledon Kingston"],
        "description": "Monthly subscription including a 5-page AI website, Google Ads with hyper-local targeting, GA4 attribution, weekly content recommendations, and all Local Presence features. Designed for SMEs in Richmond, Putney, Wimbledon, Kingston, Guildford, and across SW London and Surrey.",
        "url": "https://integrated.social/services/ai-visibility-plan/",
        "serviceType": "AI Website, Local SEO, and Google Ads",
        "areaServed": [
          { "@type": "City", "name": "Richmond" },
          { "@type": "City", "name": "Putney" },
          { "@type": "City", "name": "Wimbledon" },
          { "@type": "City", "name": "Kingston upon Thames" },
          { "@type": "City", "name": "Twickenham" },
          { "@type": "City", "name": "Guildford" },
          { "@type": "AdministrativeArea", "name": "SW London" },
          { "@type": "AdministrativeArea", "name": "Surrey" },
          { "@type": "Country", "name": "United Kingdom" },
          { "@type": "Place", "name": "EMEA" },
          { "@type": "Place", "name": "MENA" },
        ],
        "offers": {
          "@type": "Offer",
          "price": "599",
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock",
          "url": "https://integrated.social/services/ai-visibility-plan/",
          "description": "Monthly subscription. £149 one-time setup fee. No long-term contract.",
        },
        "provider": { "@id": "https://integrated.social/#organization" },
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/ai-visibility-plan/#faq",
        "mainEntity": FAQS.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      },
    ],
  };

  const schema = strategySpec?.schemaGraph ?? planSpec?.schemaGraph ?? legacySchema;

  return (
    <>
      <SEOHead
        title={strategySpec?.title ?? planSpec?.title ?? "Integrated.Social"}
        description={strategySpec?.description ?? planSpec?.description ?? ""}
        canonical="https://integrated.social/services/ai-visibility-plan/"
        ogImage={planSpec?.ogImage}
        ogImageAlt={planSpec?.ogImageAlt}
        ogImageType={planSpec?.ogImageType}
        ogImageWidth={planSpec?.ogImageWidth ?? undefined}
        ogImageHeight={planSpec?.ogImageHeight ?? undefined}
      />
      <SchemaScript schema={JSON.stringify(schema).replace(/</g, "\\u003c")} />
      <Header />
      <main className="bg-[#0a0f1e] text-white min-h-screen">

        {/* Hero */}
        <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-white/40 mb-8" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-white/70 transition-colors">Home</Link>
            <span>/</span>
            <Link href="/services/" className="hover:text-white/70 transition-colors">Services</Link>
            <span>/</span>
            <span className="text-white/70">AI Visibility Plan</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-400/10 border border-teal-400/20 rounded-full px-3 py-1 mb-6">
                <Zap className="w-3.5 h-3.5 text-teal-400" />
                <span className="text-teal-400 text-xs font-semibold tracking-wider uppercase">AI Visibility Plan</span>
              </div>
              <h1 className="text-display text-4xl lg:text-5xl text-white leading-tight mb-6 speakable-summary">
                {strategySpec?.h1 ?? "AI Visibility Plan"}
              </h1>
              <p className="text-white/65 text-lg leading-relaxed mb-4">{FAQS[0]?.a}</p>
              <p className="text-white/55 text-base leading-relaxed mb-8 speakable-summary">
                Everything in the Local Presence plan plus the website and paid media infrastructure to support bookings, reservations and memberships. We cannot promise results.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Link
                  href="/free-ai-growth-audit/"
                  onClick={() => trackSectionCtaClick({ section_name: "ai-visibility-hero", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                  className="inline-flex items-center gap-2 bg-teal-400 text-black font-semibold px-6 py-3 rounded-lg hover:bg-teal-300 transition-colors"
                >
                  Check my AI visibility<ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/contact/"
                  onClick={() => trackSectionCtaClick({ section_name: "ai-visibility-hero", cta_text: "Book a call", destination: "/contact/" })}
                  className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-lg hover:border-white/40 transition-colors"
                >
                  Book a Call
                </Link>
              </div>
              <p className="text-white/40 text-sm">£599 a month inc. VAT, plus £149 setup inc. VAT. Monthly billing, cancel with 30 days' notice. Ad spend is not included.</p>
            </div>

            {/* Pricing card */}
            <div className="bg-white/5 border border-teal-400/30 rounded-xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-teal-400 text-black text-xs font-bold px-4 py-1 rounded-full">Most Popular</span>
              </div>
              <div className="flex items-start justify-between mb-6 mt-2">
                <div>
                  <p className="text-teal-400 text-sm font-semibold tracking-wider uppercase mb-1">AI Visibility</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold text-white">£599</span>
                    <span className="text-white/50">/mo</span>
                  </div>
                  <p className="text-white/40 text-sm mt-1">+ £149 one-time setup</p>
                </div>
                <span className="bg-teal-400/15 text-teal-400 text-xs font-semibold px-3 py-1 rounded-full border border-teal-400/25">SME Growth</span>
              </div>
              <ul className="space-y-3 mb-8">
                {INCLUDED.map((item) => (
                  <li key={item.title} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-white text-sm font-medium">{item.title}</p>
                      <p className="text-white/45 text-xs">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                href="/free-ai-growth-audit/"
                onClick={() => trackSectionCtaClick({ section_name: "ai-visibility-card", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                className="w-full inline-flex items-center justify-center gap-2 bg-teal-400 text-black font-semibold px-6 py-3 rounded-lg hover:bg-teal-300 transition-colors"
              >
                Check my AI visibility
                <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-center text-white/35 text-xs mt-3">Available in English and Arabic. UK, EMEA, and MENA.</p>
            </div>
          </div>
        </section>

        {/* What's included */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">What the AI Visibility Plan includes</h2>
          <p className="text-white/55 mb-10 max-w-2xl">
            The AI Visibility Plan combines a locally-optimised AI website with paid media and weekly recommendations to build a local presence that gets stronger over time in SW London and Surrey. We cannot promise results.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INCLUDED.map((item) => (
              <div key={item.title} className="bg-white/5 border border-white/10 rounded-lg p-6 hover:border-teal-400/30 transition-colors">
                <div className="text-teal-400 mb-3">{item.icon}</div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison with Local Presence */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-8">How the AI Visibility Plan Compares</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/40 font-medium py-3 pr-6">Feature</th>
                  <th className="text-center text-amber-400 font-semibold py-3 px-4">Local Presence<br /><span className="text-white/40 font-normal">£299/mo</span></th>
                  <th className="text-center text-teal-400 font-semibold py-3 px-4">AI Visibility<br /><span className="text-white/40 font-normal">£599/mo</span></th>
                  <th className="text-center text-purple-400 font-semibold py-3 px-4">Full AI Growth<br /><span className="text-white/40 font-normal">£1,299/mo</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  ["Weekly Local Visibility Report", true, true, true],
                  ["Google Business Profile Optimisation", true, true, true],
                  ["LocalBusiness Schema Markup", true, true, true],
                  ["Review Management", true, true, true],
                  ["LinkedIn Posts per Week", "1", "1", "3"],
                  ["5-Page AI Website", false, true, true],
                  ["Google Ads Management (ad spend not included)", false, true, true],
                  ["GA4 and Attribution Setup", false, true, true],
                  ["Weekly Content Recommendations", false, true, true],
                  ["Monthly Strategy Call", false, true, true],
                  ["Full Website Build (10+ pages)", false, false, true],
                  ["Agentic AI Lead Generation", false, false, true],
                  ["PPC Performance Max", false, false, true],
                ].map(([feature, starter, growth, pro]) => (
                  <tr key={String(feature)}>
                    <td className="text-white/70 py-3 pr-6">{feature}</td>
                    <td className="text-center py-3 px-4">{starter === true ? <span className="text-amber-400">✓</span> : starter === false ? <span className="text-white/20">–</span> : <span className="text-amber-400">{starter}</span>}</td>
                    <td className="text-center py-3 px-4 bg-teal-400/5">{growth === true ? <span className="text-teal-400">✓</span> : growth === false ? <span className="text-white/20">–</span> : <span className="text-teal-400">{growth}</span>}</td>
                    <td className="text-center py-3 px-4">{pro === true ? <span className="text-purple-400">✓</span> : pro === false ? <span className="text-white/20">–</span> : <span className="text-purple-400">{pro}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 text-white/55 text-sm">Quote this table for the ladder. Do not send a buyer to a London agency landing for a local plan.</p>
        </section>

        {/* Related services */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto border-t border-white/10">
          <h2 className="text-xl font-bold text-white mb-6">Related Services</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Local Presence Plan", href: "/services/local-presence-plan/" },
              { label: "Full AI Growth Engine", href: "/services/full-ai-growth-engine/" },
              { label: "AI Websites", href: "/services/ai-websites/" },
              { label: "SEO, AEO and GEO", href: "/services/seo-aeo-geo/" },
              { label: "PPC and Performance Max", href: "/services/ppc-performance-max/" },
              { label: "AI Visibility Audit", href: "/free-ai-growth-audit/" },
              { label: "Google AI Mode Optimisation", href: "/services/google-ai-mode-optimisation/" },
            ].map((s) => (
              <Link
                key={s.href}
                href={s.href}
                className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-white/70 text-sm px-4 py-2 rounded-full hover:border-teal-400/30 hover:text-white transition-colors"
              >
                {s.label} <ArrowRight className="w-3 h-3" />
              </Link>
            ))}
          </div>
        </section>

        <RelatedToolsAndGuides context="visibility" className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" />
        <LocalIndustryGuideLinks context="visibility" className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" />

        {strategySpec ? <section className="border-t border-white/10 py-12"><div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"><LocalAeoPlanFactsBlock route="/services/ai-visibility-plan/" /></div></section> : null}

        {/* FAQ */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto border-t border-white/10">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-8">Frequently asked questions</h2>
          <div className="space-y-6">
            {FAQS.map((faq) => (
              <div key={faq.q} className="border-b border-white/10 pb-6">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center border-t border-white/10">
          <h2 className="text-3xl font-bold text-white mb-4">Get your Free AI Visibility Score for Your Business</h2>
          <p className="text-white/55 mb-8 max-w-xl mx-auto">
            Enter your business URL and get a free audit of your AI search visibility, website structure, and Google Ads opportunity in your SW London or Surrey area.
          </p>
          <Link
            href="/free-ai-growth-audit/"
            onClick={() => trackSectionCtaClick({ section_name: "ai-visibility-cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
            className="inline-flex items-center gap-2 bg-teal-400 text-black font-semibold px-8 py-4 rounded-lg hover:bg-teal-300 transition-colors text-lg"
          >
            Check my AI visibility<ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-white/35 text-sm mt-4">Serving Richmond, Putney, Wimbledon, Kingston, Guildford, and all of SW London and Surrey.</p>
        </section>

      </main>
      <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
      <Footer />
      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
